export const calculateHeatmap = (
  core_data,
  filtered_data,
  anchor,
  outcomeQ,
  outcomeStandards,
  selectedOutcomes = [],
  onlyShowOutcome = false
) => {
  // Gather the anchor options
  let anchorCategory = null;
  let anchor_options = null;
  let effectiveAnchor = anchor;

  // If anchor is set, try to find it
  if (anchor != null && anchor !== undefined && core_data?.categories?.[0]?.categories) {
    anchorCategory = core_data.categories[0].categories.find(
      (f) => f.id == anchor
    );
    anchor_options = anchorCategory?.options;
  }

  // Fallback: if anchor is null/undefined OR category not found, use primary anchor
  if (!anchor_options && core_data?.categories?.[0]?.categories) {
    anchorCategory = core_data.categories[0].categories.find(
      (f) => f.priority === "primary"
    );
    if (anchorCategory) {
      anchor_options = anchorCategory.options;
      effectiveAnchor = anchorCategory.id;
    }
  }

  // Early return if anchor_options is not available
  if (!anchor_options || !Array.isArray(anchor_options)) {
    return [];
  }

  const rawData = filtered_data[0];
  // Build a flat list of just the outcome question IDs to filter by
  const selectedOutcomeIds = Array.isArray(selectedOutcomes)
    ? selectedOutcomes
        .map((o) => (o && o.id != null ? Number(o.id) : Number(o)))
        .filter((id) => !isNaN(id))
    : [];

  // Filter raw data to only include items that responded to each selected outcome
  let dataToUse = rawData;
  if (selectedOutcomeIds.length > 0) {
    dataToUse = rawData.filter((item) =>
      selectedOutcomeIds.every((qId) =>
        item.employee_outcomes?.responses?.some(
          (r) =>
            Number(r.s) === qId &&
            (r.clicked === true || r.clicked === undefined)
        )
      )
    );
  }

  // If onlyShowOutcome is true, return exclusively outcome data
  if (onlyShowOutcome) {
    // Build a filtered outcomeQ object, containing only selected outcomes if any
    let filteredOutcomeQ = outcomeQ;
    if (outcomeQ && outcomeQ.questions && selectedOutcomeIds.length > 0) {
      filteredOutcomeQ = {
        ...outcomeQ,
        questions: outcomeQ.questions.filter((q) =>
          selectedOutcomeIds.includes(Number(q.id))
        ),
      };
    }
    return anchor_options.map((option) => {
      // Compute outcome scores for this option
      const outcomeResult = get_outcome_scores(
        option,
        [dataToUse],
        effectiveAnchor,
        filteredOutcomeQ
      );
      const overallQuestion = get_overall_question_score({
        option,
        filtered_data: [dataToUse],
        anchor: effectiveAnchor,
        includeCulture: false,
        includeOutcomes: true,
        outcomeQ: filteredOutcomeQ,
        restrictOutcomeSubquestionsToSelected: selectedOutcomeIds.length > 0,
        selectedOutcomeSubquestionIds: selectedOutcomeIds,
      });
      // Count responses matching this anchor option
      const responseCount = dataToUse.filter(
        (f) => f.categories.find((i) => i.id === effectiveAnchor)?.response === option.id
      ).length;
      return {
        option: option.name,
        outcome_scores: outcomeResult,
        overall_question_score: overallQuestion.score,
        overall_question_score_count: overallQuestion.count,
        responses: responseCount,
      };
    });
  }

  const data = anchor_options?.map((option) => {
    let culture_scores = get_culture_scores(
      option,
      [dataToUse],
      effectiveAnchor,
      core_data.questions
    );
    let outcome_scores = get_outcome_scores(
      option,
      [dataToUse],
      effectiveAnchor,
      outcomeQ
    );
    let EQ_scores = get_EQ_scores(option, [dataToUse], effectiveAnchor);
    let EQ360_scores = get_EQ360_scores(option, [dataToUse], effectiveAnchor);
    const overallQuestion = get_overall_question_score({
      option,
      filtered_data: [dataToUse],
      anchor: effectiveAnchor,
      includeCulture: true,
      includeOutcomes: !!outcomeQ,
      outcomeQ,
      restrictOutcomeSubquestionsToSelected: false,
      selectedOutcomeSubquestionIds: selectedOutcomeIds,
    });
    return {
      option: option.name,
      culture_scores: culture_scores,
      outcome_scores: outcome_scores,
      EQ_scores: EQ_scores,
      EQ360_scores: EQ360_scores,
      overall_question_score: overallQuestion.score,
      overall_question_score_count: overallQuestion.count,
      responses: dataToUse.filter(
        (f) => f.categories.find((i) => i.id === effectiveAnchor)?.response === option.id
      ).length,
    };
  });

  return data;
};

const to_number_or_null = (v) => {
  if (v === undefined || v === null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Pooled "overall question" score for the heatmap.
 * - Culture: all valid culture item responses in `data.questions[].response`
 * - Outcomes: all valid outcome responses in `data.employee_outcomes.responses[].response`
 *
 * This intentionally excludes EQ / EQ360 (they're not survey questions).
 */
const get_overall_question_score = ({
  option,
  filtered_data,
  anchor,
  includeCulture,
  includeOutcomes,
  outcomeQ,
  restrictOutcomeSubquestionsToSelected = false,
  selectedOutcomeSubquestionIds = [],
}) => {
  const anchor_data = filtered_data[0].filter(
    (f) => f.categories.find((i) => i.id == anchor)?.response == option.id
  );

  const values = [];

  if (includeCulture) {
    anchor_data.forEach((data) => {
      if (!Array.isArray(data?.questions)) return;
      data.questions.forEach((q) => {
        const n = to_number_or_null(q?.response);
        if (n != null) values.push(n);
      });
    });
  }

  if (includeOutcomes && outcomeQ?.questions) {
    const allowedOutcomeIds = new Set(
      outcomeQ.questions.map((q) => Number(q.id)).filter((id) => !isNaN(id))
    );
    const selectedSet = new Set(
      (selectedOutcomeSubquestionIds || [])
        .map((id) => Number(id))
        .filter((id) => !isNaN(id))
    );

    anchor_data.forEach((data) => {
      const responses = data?.employee_outcomes?.responses;
      if (!Array.isArray(responses)) return;

      responses.forEach((r) => {
        if (!(r?.clicked === true || r?.clicked === undefined)) return;
        const qId = Number(r?.q);
        const sId = Number(r?.s);
        if (!allowedOutcomeIds.has(qId)) return;
        if (restrictOutcomeSubquestionsToSelected && selectedSet.size > 0) {
          if (!selectedSet.has(sId)) return;
        }
        const n = to_number_or_null(r?.response);
        if (n != null) values.push(n);
      });
    });
  }

  return {
    score: values.length > 0 ? average_array(values) : null,
    count: values.length,
  };
};

const get_culture_scores = (option, filtered_data, anchor, questions) => {
  let anchor_data = filtered_data[0].filter(
    (f) => f.categories.find((i) => i.id == anchor)?.response == option.id
  );
  let overall_scores = [];

  // Get the overall culture scores - filter out undefined/null/NaN responses (but include 0 values)
  anchor_data.forEach((data) => {
    const validResponses = data.questions
      .map((question) => question.response)
      .filter(
        (response) =>
          response !== undefined && response !== null && !isNaN(response)
      );
    if (validResponses.length > 0) {
      overall_scores.push(...validResponses);
    }
  });

  // Get the factor scores
  let factor_scores = {};
  let item_scores = {};

  questions?.dimensions.forEach((dimension) => {
    dimension.factors.forEach((factor) => {
      let scores = [];
      const dimension_id = dimension.id - 1;
      const factor_id = factor.id - 1;

      anchor_data.forEach((data) => {
        const factorResponses = data.questions
          .filter((i) => i.id == dimension_id && i.factor == factor_id)
          .map((r) => r.response)
          .filter(
            (response) =>
              response !== undefined && response !== null && !isNaN(response)
          );

        if (factorResponses.length > 0) {
          scores.push(...factorResponses);
        }
      });

      const key = JSON.stringify([dimension_id, factor_id]);
      factor_scores[key] = {
        scores: scores.length > 0 ? average_array(scores) : null,
        factor: factor.title,
      };

      factor.questions.forEach((question, q_id) => {
        let item_scores_raw = anchor_data
          .map((data) => {
            return data.questions.find(
              (i) =>
                i.id == dimension_id && i.factor == factor_id && i.q == q_id
            )?.response;
          })
          .filter((f) => f != undefined && f !== null && !isNaN(f));

        const item_key = JSON.stringify([dimension_id, factor_id, q_id]);
        item_scores[item_key] = {
          scores:
            item_scores_raw.length > 0 ? average_array(item_scores_raw) : null,
          question: question.q,
          factor_name: factor.title,
          dimension_name: dimension.title,
        };
      });
    });
  });

  return {
    overall_scores:
      overall_scores.length > 0 ? average_array(overall_scores) : null,
    factor_scores: factor_scores,
    item_scores: item_scores,
  };
};

const average_array = (arr) => {
  return arr.reduce((a, b) => a + b, 0) / arr.length;
};

const get_outcome_scores = (option, filtered_data, anchor, outcomeQ) => {
  let outcome_scores = {};
  let question_scores = {};

  // If no outcomeQ provided, return empty structure
  if (!outcomeQ || !outcomeQ.questions) {
    return {
      overall_scores: outcome_scores,
      question_scores: question_scores,
    };
  }

  let anchor_data = filtered_data[0].filter(
    (f) => f.categories.find((i) => i.id == anchor)?.response == option.id
  );

  outcomeQ.questions.forEach((question) => {
    let name = question.name;
    let filtered_responses = anchor_data
      .map((data) =>
        data?.employee_outcomes?.responses
          ?.filter((i) => {
            return (
              i.q == question.id &&
              (i?.clicked === true || i?.clicked === undefined)
            );
          })
          .map((i) => i?.response)
      )
      .flat()
      .filter((f) => !isNaN(f) && f !== undefined && f !== null);

    let scores =
      filtered_responses.length > 0 ? average_array(filtered_responses) : null;
    outcome_scores[name] = scores;

    question.questions.forEach((q) => {
      let filtered_responses = anchor_data
        .map((data) =>
          data?.employee_outcomes?.responses
            ?.filter((i) => {
              return (
                i.q == question.id &&
                i.s == q.s &&
                (i?.clicked === true || i?.clicked === undefined)
              );
            })
            .map((i) => i?.response)
        )
        .flat()
        .filter((f) => !isNaN(f) && f !== undefined && f !== null);

      let scores =
        filtered_responses.length > 0
          ? average_array(filtered_responses)
          : null;
      const item_key = JSON.stringify([q.q, q.s]);
      question_scores[item_key] = {
        scores,
        question: q.question,
        outcome: name,
      };
    });
  });

  return {
    overall_scores: outcome_scores,
    question_scores: question_scores,
  };
};

const aggregate_vertical_scores = (data) => {
  // Sum of outcome scores (or zero if none)
  const outcomeValues = Object.values(
    data.outcome_scores?.overall_scores || {}
  );
  const outcomeSum = outcomeValues.reduce((sum, v) => sum + (v || 0), 0);

  // If culture scores are missing, use outcome sum
  const cultureScore = data.culture_scores?.overall_scores;
  if (cultureScore == null || isNaN(cultureScore)) {
    return outcomeSum;
  }

  // If outcome scores are missing or invalid, return culture score
  const hasValidOutcomes =
    outcomeValues.length > 0 &&
    !outcomeValues.some((v) => v == null || isNaN(v));
  if (!hasValidOutcomes) {
    return cultureScore;
  }

  // Otherwise, return combined culture + outcome sum
  return cultureScore + outcomeSum;
};

export const vertical_sorted_names = (data) => {
  // Preserve anchor/order as provided; do NOT sort horizontally.
  return data.map((item) => item.option);
};

export const horizontal_sorted_names = (data, includeOutcomes = true, rawData = null, outcomeQ = null) => {
  // Calculate totals safely
  const totalResponses = data.reduce(
    (acc, item) => acc + (item.responses || 0),
    0
  );
  // Build list of outcome columns if requested
  let cols = [];

  if (includeOutcomes && data[0]?.outcome_scores?.overall_scores) {
    const names = Object.keys(data[0].outcome_scores.overall_scores);
    
    // Calculate outcome totals from ALL raw question responses across ALL anchors
    // This ensures the outcome total matches the question-level calculations
    if (rawData && Array.isArray(rawData[0]) && outcomeQ) {
      const allRawResponses = rawData[0];
      
      names.forEach((name) => {
        const allOutcomeResponses = [];
        
        // Find the outcome question ID for this outcome name
        const outcomeQuestion = outcomeQ.questions.find((q) => q.name === name);
        
        if (outcomeQuestion) {
          const questionId = outcomeQuestion.id;
          
          // Collect ALL outcome responses for this outcome from ALL responses (not filtered by anchor)
          allRawResponses.forEach((response) => {
            if (!Array.isArray(response?.employee_outcomes?.responses)) return;
            response.employee_outcomes.responses.forEach((r) => {
              if (r.q == questionId && (r?.clicked === true || r?.clicked === undefined)) {
                const n = to_number_or_null(r?.response);
                if (n != null) {
                  allOutcomeResponses.push(n);
                }
              }
            });
          });
        }
        
        // Calculate average directly from all raw responses
        const outcomeScore = allOutcomeResponses.length > 0 
          ? average_array(allOutcomeResponses) 
          : null;
        
        cols.push({ name, score: outcomeScore });
      });
    } else {
      // Fallback to old method if raw data not available
      names.forEach((name) => {
        let weightedSum = 0;
        let respSum = 0;
        data.forEach((item) => {
          const val = item.outcome_scores.overall_scores[name];
          if (val != null && !isNaN(val)) {
            weightedSum += val * (item.responses || 0);
            respSum += item.responses || 0;
          }
        });
        const avg = respSum > 0 ? weightedSum / respSum : null;
        cols.push({ name, score: avg });
      });
    }
  }

  // EQ and EQ360
  if (data[0]?.EQ_scores && Object.keys(data[0].EQ_scores).length > 0) {
    const eqAvg =
      data.reduce((acc, item) => {
        const v = item.EQ_scores.Overall;
        return acc + (v != null && !isNaN(v) ? v : 0);
      }, 0) / data.length;
    cols.push({ name: "Emotional Intelligence", score: eqAvg });
  }
  if (data[0]?.EQ360_scores && Object.keys(data[0].EQ360_scores).length > 0) {
    const eq360Avg =
      data.reduce((acc, item) => {
        const v = item.EQ360_scores.Overall;
        return acc + (v != null && !isNaN(v) ? v : 0);
      }, 0) / data.length;
    cols.push({ name: "Emotional Intelligence 360", score: eq360Avg });
  }

  // Finally, culture if available
  if (data[0]?.culture_scores?.overall_scores != null) {
    const totalCulture = data.reduce((acc, item) => {
      const v = item.culture_scores.overall_scores;
      return acc + (v != null && !isNaN(v) ? v * (item.responses || 0) : 0);
    }, 0);
    const avgCulture =
      totalResponses > 0 ? totalCulture / totalResponses : null;
    cols.push({ name: "Culture", score: avgCulture });
  }

  // Preserve constructed order (no per-anchor resorting); Culture is appended last
  return cols;
};

export const factorTitleSorted = (data, selectedFactors = [], rawData = null, questions = null) => {
  // If there's no culture data, skip factor titles
  if (!data[0]?.culture_scores?.factor_scores) {
    return [];
  }
  let factor_scores = [];

  if (data.length == 0) {
    return [];
  }

  let factor_titles = Object.values(data[0].culture_scores.factor_scores).map(
    (item) => item.factor
  );

  // Filter factor titles based on selected factors if any are provided
  if (selectedFactors.length > 0) {
    const selectedFactorNames = selectedFactors.map((f) => f.name || f.title);
    factor_titles = factor_titles.filter((title) =>
      selectedFactorNames.includes(title)
    );
  }

  // Calculate factor totals from ALL raw question responses across ALL anchors
  // This ensures the factor total matches the question-level calculations
  if (rawData && Array.isArray(rawData[0]) && questions) {
    const allRawResponses = rawData[0];
    
    factor_titles.forEach((title) => {
      const allFactorResponses = [];
      
      // Find the dimension and factor IDs for this factor title
      questions.dimensions.forEach((dimension) => {
        dimension.factors.forEach((factor) => {
          if (factor.title === title) {
            const dimension_id = dimension.id - 1;
            const factor_id = factor.id - 1;
            
            // Collect ALL question responses for this factor from ALL responses (not filtered by anchor)
            allRawResponses.forEach((response) => {
              if (!Array.isArray(response?.questions)) return;
              response.questions.forEach((q) => {
                if (q.id == dimension_id && q.factor == factor_id) {
                  const n = q?.response;
                  if (n != null && n !== 'N/A' && !isNaN(n) && typeof n === 'number') {
                    allFactorResponses.push(n);
                  }
                }
              });
            });
          }
        });
      });
      
      // Calculate average directly from all raw responses
      const factorScore = allFactorResponses.length > 0 
        ? average_array(allFactorResponses) 
        : null;
      
      factor_scores.push({ scores: factorScore, name: title });
    });
  } else {
    // Fallback to old method if raw data not available
    const total_responses = data
      .map((item) => item.responses)
      .reduce((a, b) => a + b, 0);

    factor_titles.forEach((title) => {
      let factor_total = 0;
      let total_weight = 0;
      data.forEach((item) => {
        let score = Object.values(item.culture_scores.factor_scores).find(
          (f) => f.factor == title
        )?.scores;
        let respondents = item.responses;
        if (score != null && !isNaN(score)) {
          factor_total += score * respondents;
          total_weight += respondents;
        }
      });

      factor_scores.push({ 
        scores: total_weight > 0 ? factor_total / total_weight : null, 
        name: title 
      });
    });
  }

  // Best → worst to align with CEO guidance (worst bottom-right).
  return factor_scores.sort((a, b) => {
    const aScore = a.scores != null ? a.scores : -Infinity;
    const bScore = b.scores != null ? b.scores : -Infinity;
    return bScore - aScore;
  });
};

export const addEQ = (data) => {
  const factors = [
    "Experiential EQ",
    "Strategic EQ",
    "Self Awareness",
    "Self Management",
    "Empathy",
    "Social Awareness",
    "Relationship Management",
    "Self-Regard",
    "Self-Actualization",
    "Reality Testing",
    "Impulse Control",
    "Optimism",
    "Social Responsibility",
    "Emotional Expression",
    "Independence",
    "Emotional Self-Awareness",
    "Assertiveness",
    "Emotional Empathy",
    "Cognitive Empathy",
    "Interpersonal Relationships",
    "Stress Tolerance",
    "Flexibility",
  ];

  const EQ360 = [
    "Empathy Score",
    "Experiential EQ",
    "Overall Score",
    "Relationship Management",
    "Self-Awareness",
    "Self-Management",
    "Social Awareness",
    "Strategic EQ",
  ];

  data[0].map((item) => {
    if (item?.employee_outcomes?.responses) {
      item["EQ"] = [];

      const score = item.employee_outcomes?.responses[1].response;
      const correlation = 0.8;
      const stdDevX = 2.89;

      factors.forEach((factor) => {
        const randomError =
          Math.random() * Math.sqrt(1 - Math.pow(correlation, 2)) * stdDevX;
        const newScore = correlation * score + randomError;
        item["EQ"].push({ name: factor, score: newScore });
      });

      item["EQ"].push({
        name: "Overall",
        score: average_array(item["EQ"].map((i) => i.score)),
      });

      item["EQ360"] = [];
      const correlation2 = 0.7;
      const stdDevX2 = 3.5;

      EQ360.forEach((factor) => {
        const randomError =
          Math.random() * Math.sqrt(1 - Math.pow(correlation, 2)) * stdDevX2;
        const newScore = correlation2 * score + randomError;

        item["EQ360"].push({ name: factor, score: newScore });
      });

      item["EQ360"].push({
        name: "Overall",
        score: average_array(item["EQ360"].map((i) => i.score)),
      });

      return item;
    }
  });
  return data;
};

const get_EQ_scores = (option, filtered_data, anchor) => {
  let anchor_data = filtered_data[0]
    .filter((f) => f.EQ)
    .filter(
      (f) => f.categories.find((i) => i.id == anchor)?.response == option.id
    );
  let EQ_scores = {};

  let factors = anchor_data[0]?.EQ.map((data) => data.name);

  factors?.forEach((factor) => {
    let scores = [];
    anchor_data.forEach((data) => {
      const score = data.EQ.find((i) => i.name == factor)?.score;
      if (score !== undefined && score !== null && !isNaN(score)) {
        scores.push(score);
      }
    });
    EQ_scores[factor] = scores.length > 0 ? average_array(scores) : null;
  });

  return EQ_scores;
};

const get_EQ360_scores = (option, filtered_data, anchor) => {
  let anchor_data = filtered_data[0]
    .filter((f) => f.EQ360)
    .filter(
      (f) => f.categories.find((i) => i.id == anchor)?.response == option.id
    );
  let EQ_scores = {};

  let factors = anchor_data[0]?.EQ360.map((data) => data?.name);

  factors?.forEach((factor) => {
    let scores = [];
    anchor_data.forEach((data) => {
      const score = data.EQ360.find((i) => i.name == factor)?.score;
      if (score !== undefined && score !== null && !isNaN(score)) {
        scores.push(score);
      }
    });
    EQ_scores[factor] = scores.length > 0 ? average_array(scores) : null;
  });

  return EQ_scores;
};
